"use client";

import { motion, useScroll, useTransform } from "motion/react";
import { ChevronDown, ArrowDown } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import Image from "next/image";
import countries from "world-countries";
import { ThankYouModal } from "./ThankYouModal";
import { trackFormConversion, getUTMParameters } from "../utils/analytics";
import { detectUserCountry } from "../utils/countryDetection";

export function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const [phone, setPhone] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState("AE");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileForm, setShowMobileForm] = useState(true);
  const [expandMobileForm, setExpandMobileForm] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  // Function to get flag emoji from country code
  const getFlagEmoji = (countryCode: string) => {
    return countryCode
      .toUpperCase()
      .replace(/./g, (char) =>
        String.fromCodePoint(127397 + char.charCodeAt(0))
      );
  };

  // Prepare country options with calling codes
  const countryOptions = countries
    .filter((country) => country.idd.root)
    .map((country) => ({
      code: country.cca2,
      name: country.name.common,
      dialCode: `${country.idd.root}${country.idd.suffixes?.[0] || ""}`,
      flag: getFlagEmoji(country.cca2),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Filter countries based on search
  const filteredCountries = countryOptions.filter(
    (country) =>
      country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.dialCode.includes(searchQuery)
  );

  // Get selected country details
  const selectedCountry = countryOptions.find(
    (c) => c.code === selectedCountryCode
  );

  // Detect user's country using IP detection with fallbacks - non-blocking
  useEffect(() => {
    let mounted = true;

    const detectCountry = async () => {
      try {
        const detectedCountry = await detectUserCountry();
        if (mounted && detectedCountry !== selectedCountryCode) {
          setSelectedCountryCode(detectedCountry);
        }
      } catch (error) {
        // Silent fail to not impact user experience
        if (mounted) {
          setSelectedCountryCode('AE'); // Default to UAE
        }
      }
    };

    // Delay to not interfere with initial render/LCP
    const timer = setTimeout(detectCountry, 200);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [selectedCountryCode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const utmParams = getUTMParameters();
      const formDataToSend = new FormData();
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', `${selectedCountry?.dialCode} ${phone}`);
      formDataToSend.append('country', selectedCountry?.name || '');
      formDataToSend.append('formSource', 'Hero Section');
      formDataToSend.append('utm_id', 'LP04');
      formDataToSend.append('utm_source', 'organic_google');
      formDataToSend.append('campaign_name', 'Gate Eleven Landing Page');
      formDataToSend.append('media_source', 'Gate Eleven Residences Website');
      formDataToSend.append('method_of_contact', 'website');
      formDataToSend.append('property_ref_no.', '8478');

      const response = await fetch(
        "https://hooks.zapier.com/hooks/catch/18859173/uwfr4km/",
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      if (response.ok) {
        // Track conversion with enhanced data
        trackFormConversion("Hero Form", formData.email, `${selectedCountry?.dialCode}${phone}`);
        // Reset form
        setFormData({ firstName: "", lastName: "", email: "" });
        setPhone("");
        setExpandMobileForm(false);
        // Show thank you modal
        setShowThankYou(true);
      } else {
        throw new Error("Submission failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={ref} className="relative h-auto lg:h-[100dvh] overflow-hidden ">
      {/* Parallax Background - Lighter Overlay */}
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-black/50 md:bg-black/30 z-10" />
        <Image
          src="/images/hero.jpg"
          alt="Gate Eleven Dubai - Luxury Residences by Amwaj Development in District 11, Meydan"
          fill
          className="object-cover"
          priority
          quality={75}
          sizes="100vw"
          fetchPriority="high"
        />
      </motion.div>

      {/* Content - Minimalist */}
      <motion.div className="relative z-20 h-full lg:flex lg:items-center lg:justify-between container mx-auto px-4 md:px-6">
        {/* Left Side - Text Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.3 }}
          className="max-w-2xl pt-32 lg:pt-0"
        >
          <h1 className=" mb-8 tracking-[0.2em] text-4xl md:text-6xl lg:text-7xl font-light text-white">
            GATE ELEVEN RESIDENCES
          </h1>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="h-[1px] bg-white/40 w-full max-w-md mb-4 origin-left"
          />
          <p className="text-white text-lg lg:text-xl tracking-widest font-light mb-2">Starting from <span className="text-black font-normal py-1 px-2 bg-white/80">AED 1.6M</span></p>
          <p className="mb-8 tracking-[0.2em] text-lg lg:text-lg font-light text-white">
            DISTRICT 11, MEYDAN, DUBAI
          </p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="space-y-2 mt-2"
          >
            {[
              '1 & 2 Bedroom Apartments',
              '18 mins to Downtown Dubai',
              'Lagoon & Park Views',
              'Pay Only 30% Until Handover',
            ].map((point, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-white rounded-full flex-shrink-0" />
                <p className="text-white text-sm tracking-wider font-normal">{point}</p>
              </div>
            ))}
          </motion.div>

        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.2, delay: 0.5 }}
          className="hidden lg:block w-full max-w-md backdrop-blur-md"
        >
          <div className="bg-black/20 border border-white/20 p-8 rounded-sm shadow-2xl">
            <h2 className="text-white text-xl font-light tracking-[0.15em] mb-6">
              GET IN TOUCH
            </h2>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    placeholder="FIRST NAME"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    required
                    className="w-full bg-black/10 border border-white/30 text-white placeholder-white/50 px-4 py-3 text-base tracking-wider focus:outline-none focus:border-white/50 transition-all"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="LAST NAME"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    required
                    className="w-full bg-black/10 border border-white/30 text-white placeholder-white/50 px-4 py-3 text-base tracking-wider focus:outline-none focus:border-white/50 transition-all"
                  />
                </div>
              </div>

              <div>
                <input
                  type="email"
                  placeholder="EMAIL"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  className="w-full bg-black/10 border border-white/30 text-white placeholder-white/50 px-4 py-3 text-base tracking-wider focus:outline-none focus:border-white/50 transition-all"
                />
              </div>

              <div className="flex gap-2">
                {/* Custom Searchable Country Dropdown */}
                <div ref={dropdownRef} className="relative min-w-[120px]">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full bg-black/10 border border-white/30 text-white px-3 py-3 text-base focus:outline-none focus:border-white/50 transition-all flex items-center justify-between"
                  >
                    <span>
                      {selectedCountry?.flag} {selectedCountry?.dialCode}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""
                        }`}
                    />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-black/95 border border-white/30 backdrop-blur-md max-h-60 overflow-hidden z-50 rounded-sm">
                      <div className="p-2 border-b border-white/20">
                        <input
                          type="text"
                          placeholder="Search country..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full bg-black/20 border border-white/20 text-white placeholder-white/40 px-3 py-2 text-sm focus:outline-none focus:border-white/40"
                          autoFocus
                        />
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {filteredCountries.map((country) => (
                          <button
                            key={country.code}
                            type="button"
                            onClick={() => {
                              setSelectedCountryCode(country.code);
                              setIsDropdownOpen(false);
                              setSearchQuery("");
                            }}
                            className="w-full px-3 py-2 text-left text-white hover:bg-white/10 transition-colors flex items-center gap-2 text-sm"
                          >
                            <span>{country.flag}</span>
                            <span className="text-white/60">
                              {country.dialCode}
                            </span>
                          </button>
                        ))}
                        {filteredCountries.length === 0 && (
                          <div className="px-3 py-4 text-white/40 text-sm text-center">
                            No countries found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <input
                  type="tel"
                  placeholder="MOBILE NUMBER"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="flex-1 bg-black/10 border border-white/30 text-white placeholder-white/50 px-4 py-3 text-base tracking-wider focus:outline-none focus:border-white/50 transition-all"
                />
              </div>

              {/* Terms and Privacy Notice */}
              <div className="text-center mt-4 mb-2">
                <p className="text-white/60 text-xs">
                  By clicking Submit, you agree to our{' '}
                  <a href="/terms-and-conditions" className="text-white/80 hover:text-white underline transition-colors">
                    Terms & Conditions
                  </a>
                  {' '}and{' '}
                  <a href="/privacy-policy" className="text-white/80 hover:text-white underline transition-colors">
                    Privacy Policy
                  </a>
                </p>
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/40 px-6 py-3 tracking-[0.2em] text-sm transition-all duration-300 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "SUBMITTING..." : "SUBMIT"}
              </motion.button>
            </form>
          </div>
        </motion.div>

        {/* Mobile Form - Bottom */}
        {showMobileForm && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="lg:hidden my-6 "
          >
            <div className="relative">              {/* Selling Points - Mobile Only, Above More Info */}

              <div className="bg-black/10 border border-white/20 p-4  shadow-2xl backdrop-blur-md">
                <form className="" onSubmit={handleSubmit}>
                  {/* Email Field - Always Visible */}
                  <div>
                    <input
                      type="email"
                      placeholder="EMAIL"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                      }}
                      required
                      className="w-full bg-white/10 border border-white/30 text-white placeholder-white/50 px-4 py-3 text-base tracking-wider focus:outline-none focus:border-white/50 transition-all "
                    />
                  </div>

                  {/* Expanded Fields - Always visible on mobile now */}
                  <motion.div
                    initial={{ height: "auto", opacity: 1 }}
                    animate={{
                      height: "auto",
                      opacity: 1,
                    }}
                    transition={{ duration: 0.4 }}
                    className="space-y-3"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <div className="h-0 w-full col-span-2"></div>
                      <input
                        type="text"
                        placeholder="FIRST NAME"
                        value={formData.firstName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            firstName: e.target.value,
                          })
                        }
                        required
                        className="w-full bg-white/10 border border-white/30 text-white placeholder-white/50 px-4 py-3 text-base tracking-wider focus:outline-none focus:border-white/50 transition-all "
                      />
                      <input
                        type="text"
                        placeholder="LAST NAME"
                        value={formData.lastName}
                        onChange={(e) =>
                          setFormData({ ...formData, lastName: e.target.value })
                        }
                        required
                        className="w-full bg-white/10 border border-white/30 text-white placeholder-white/50 px-4 py-3 text-base tracking-wider focus:outline-none focus:border-white/50 transition-all "
                      />
                    </div>

                    <div className="flex gap-2">
                      {/* Country Dropdown */}
                      <div ref={dropdownRef} className="relative min-w-[100px]">
                        <button
                          type="button"
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className="w-full bg-white/10 border border-white/30 text-white px-2 py-3 text-base focus:outline-none focus:border-white/50 transition-all flex items-center justify-between "
                        >
                          <span className="text-base">
                            {selectedCountry?.flag} {selectedCountry?.dialCode}
                          </span>
                          <ChevronDown
                            className={`w-3 h-3 transition-transform ${isDropdownOpen ? "rotate-180" : ""
                              }`}
                          />
                        </button>

                        {isDropdownOpen && (
                          <div className="absolute bottom-full left-0 right-0 mb-1 bg-black/95 border border-white/30 backdrop-blur-md  max-h-48 overflow-hidden z-50">
                            <div className="p-2 border-b border-white/20">
                              <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-black/20 border border-white/20 text-white placeholder-white/40 px-2 py-1 text-xs focus:outline-none focus:border-white/40 "
                                autoFocus
                              />
                            </div>
                            <div className="overflow-y-auto max-h-36 ">
                              {filteredCountries.map((country) => (
                                <button
                                  key={country.code}
                                  type="button"
                                  onClick={() => {
                                    setSelectedCountryCode(country.code);
                                    setIsDropdownOpen(false);
                                    setSearchQuery("");
                                  }}
                                  className="w-full px-2 py-2 text-left text-white hover:bg-white/10 transition-colors flex items-center gap-2 text-xs"
                                >
                                  <span>{country.flag}</span>
                                  <span className="text-white/60 text-xs">
                                    {country.dialCode}
                                  </span>
                                </button>
                              ))}
                              {filteredCountries.length === 0 && (
                                <div className="px-2 py-3 text-white/40 text-xs text-center">
                                  No countries found
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <input
                        type="tel"
                        placeholder="MOBILE NUMBER"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                        className="flex-1 bg-white/10 border w-full border-white/30 text-white placeholder-white/50 px-4 py-3 text-base tracking-wider focus:outline-none focus:border-white/50 transition-all"
                      />
                    </div>

                    {/* Terms and Privacy Notice */}
                    <div className="text-center mt-4 mb-2">
                      <p className="text-white/60 text-xs">
                        By clicking Submit, you agree to our{' '}
                        <a href="/terms-and-conditions" className="text-white/80 hover:text-white underline transition-colors">
                          Terms & Conditions
                        </a>
                        {' '}and{' '}
                        <a href="/privacy-policy" className="text-white/80 hover:text-white underline transition-colors">
                          Privacy Policy
                        </a>
                      </p>
                    </div>

                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/40 px-6 py-3 tracking-[0.2em] text-base transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? "SUBMITTING..." : "SUBMIT"}
                    </motion.button>
                  </motion.div>
                </form>
              </div>
            </div>
          </motion.div>
        )}

        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 lg:block hidden"
        >
          <ChevronDown className="w-8 h-8 text-white/60" />
        </motion.div>
      </motion.div>

      {/* Thank You Modal */}
      <ThankYouModal isOpen={showThankYou} onClose={() => setShowThankYou(false)} />
    </div>
  );
}
